
import React, { useEffect, useState } from "react";
import LessonContent from "@/components/lessons/LessonContent";
import LessonCompletionStatus from "./LessonCompletionStatus";
import LessonMaterialDisplay from "./LessonMaterialDisplay";
import { useVideoHandler } from "@/hooks/useVideoHandler";
import { useLessonCompletion } from "@/hooks/useLessonCompletion";
import { useNextLesson } from "@/hooks/useNextLesson";
import { supabase } from "@/lib/supabase";

interface LessonType {
  id: string;
  title: string;
  duration: string;
  video_id: string;
  pdf_url?: string;
  isCompleted: boolean;
  description?: string;
}

interface ModuleType {
  id: string;
  title: string;
  lessons: LessonType[];
}

interface EnrolledLessonViewProps {
  activeLesson: LessonType | null;
  modulesList: ModuleType[];
  userCourse: any;
  onLessonSelect: (lesson: LessonType) => void;
  onLessonComplete: () => Promise<any>;
  onVideoEnd: () => Promise<any>;
  courseImage: string;
}

const EnrolledLessonView = ({
  activeLesson,
  modulesList,
  userCourse,
  onLessonSelect,
  onLessonComplete,
  onVideoEnd,
  courseImage
}: EnrolledLessonViewProps) => {
  // Check if current lesson is already completed
  const isCurrentLessonCompleted = activeLesson ? 
    (userCourse?.completed_lessons || []).includes(activeLesson.id) : 
    false;

  const nextLesson = useNextLesson(activeLesson, modulesList);

  const {
    videoLoaded,
    handleVideoLoad,
    handleVideoError,
    handleVideoEndEvent
  } = useVideoHandler(onVideoEnd);

  const {
    completionInProgress,
    handleCompleteLesson
  } = useLessonCompletion(isCurrentLessonCompleted, onLessonComplete);

  // State for course materials
  const [courseMaterials, setCourseMaterials] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  // Create lesson materials array if PDF exists
  const lessonMaterials = activeLesson?.pdf_url 
    ? [{ 
        id: `pdf-${activeLesson.id}`, 
        title: `${activeLesson.title} PDF`, 
        url: activeLesson.pdf_url, 
        type: 'pdf' 
      }] 
    : [];

  // Fetch course materials
  useEffect(() => {
    const fetchCourseMaterials = async () => {
      if (!userCourse?.course_id) return;
      
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('course_materials')
          .select('*')
          .eq('course_id', userCourse.course_id);
        
        if (error) {
          console.error('Error fetching course materials:', error);
          return;
        }
        
        if (data && data.length > 0) {
          // Transform data to match the format expected by LessonMaterialsList
          const formattedMaterials = data.map(material => ({
            id: material.id,
            title: material.display_name,
            url: material.file_key.startsWith('materials//') ? 
              `https://s3.pilw.io/kaijaalliksaar/${material.file_key.replace('materials//', '')}` : 
              `https://s3.pilw.io/kaijaalliksaar/${material.file_key}`,
            type: material.file_type.includes('pdf') ? 'pdf' : 'file'
          }));
          setCourseMaterials(formattedMaterials);
        }
      } catch (err) {
        console.error('Failed to fetch course materials:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchCourseMaterials();
  }, [userCourse?.course_id]);

  if (!activeLesson) {
    return null;
  }

  const handleNextLesson = () => {
    if (nextLesson) {
      onLessonSelect(nextLesson);
    }
  };

  return (
    <>
      <LessonContent
        lessonId={activeLesson.id}
        title={activeLesson.title}
        description={activeLesson.description || ""}
        videoId={activeLesson.video_id || ""}
        posterImage={courseImage}
        onVideoEnd={handleVideoEndEvent}
        onVideoLoad={handleVideoLoad}
        onVideoError={handleVideoError}
      />
      
      {videoLoaded && (
        <LessonCompletionStatus
          isCompleted={isCurrentLessonCompleted}
          onComplete={handleCompleteLesson}
          completionInProgress={completionInProgress}
          nextLesson={nextLesson}
          onSelectNextLesson={handleNextLesson}
        />
      )}
      
      <LessonMaterialDisplay 
        lessonMaterials={lessonMaterials} 
        courseMaterials={courseMaterials}
      />
    </>
  );
};

export default EnrolledLessonView;
